/**
 * Birthday Widget Settings Component
 *
 * Provides configuration interface for the Birthday Widget tool.
 * Allows studio owners/managers to customize display settings.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Snackbar,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Palette as PaletteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import {
  useGetBirthdayWidgetConfigQuery,
  useUpdateBirthdayWidgetConfigMutation,
} from '../../api/birthdayWidgetApi';
import {
  DISPLAY_PERIOD_OPTIONS,
  NAME_FORMAT_OPTIONS,
  type BirthdayDisplayPeriod,
  type BirthdayNameFormat,
} from '../../types/birthdayWidget';

interface BirthdayWidgetSettingsProps {
  studioId: string;
}

const BirthdayWidgetSettings: React.FC<BirthdayWidgetSettingsProps> = ({
  studioId,
}) => {
  const navigate = useNavigate();
  const [copySuccess, setCopySuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch current configuration
  const {
    data: config,
    isLoading,
    error,
  } = useGetBirthdayWidgetConfigQuery(studioId, {
    skip: !studioId,
  });

  // Update mutation
  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateBirthdayWidgetConfigMutation();

  // Form state - always initialize with default values to avoid controlled/uncontrolled warning
  const [displayPeriod, setDisplayPeriod] =
    useState<BirthdayDisplayPeriod>('month');
  const [nameFormat, setNameFormat] = useState<BirthdayNameFormat>('full');
  const [greetingMessage, setGreetingMessage] = useState('Happy Birthday!');

  // Update form state when config loads
  React.useEffect(() => {
    if (config) {
      setDisplayPeriod(config.display_period);
      setNameFormat(config.name_display_format);
      setGreetingMessage(config.greeting_message);
    }
  }, [config]);

  const handleSave = async () => {
    if (!studioId) return;

    try {
      await updateConfig({
        studioId,
        data: {
          display_period: displayPeriod,
          name_display_format: nameFormat,
          greeting_message: greetingMessage,
        },
      }).unwrap();
      setSaveSuccess(true);
    } catch (err) {
      console.error('Failed to update birthday widget config:', err);
    }
  };

  const handleCopyEmbedCode = () => {
    if (config?.embed_code) {
      navigator.clipboard.writeText(config.embed_code);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleViewPublicPage = () => {
    // Extract URL from embed code or construct from studio
    if (config?.embed_code) {
      // Parse the iframe src attribute from embed code
      const match = config.embed_code.match(/src="([^"]+)"/);
      if (match && match[1]) {
        window.open(match[1], '_blank');
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSaveSuccess(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load birthday widget configuration. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Birthday Widget Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Configure how student birthdays are displayed on your public birthday
        widget.
      </Typography>

      {/* Settings Form */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Display Settings
        </Typography>

        {/* Display Period */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Display Period</InputLabel>
          <Select
            value={displayPeriod}
            label="Display Period"
            onChange={e =>
              setDisplayPeriod(e.target.value as BirthdayDisplayPeriod)
            }
          >
            {DISPLAY_PERIOD_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Box>
                  <Typography>{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Name Display Format */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Name Display Format</InputLabel>
          <Select
            value={nameFormat}
            label="Name Display Format"
            onChange={e => setNameFormat(e.target.value as BirthdayNameFormat)}
          >
            {NAME_FORMAT_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Box>
                  <Typography>{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Example: {option.example}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Greeting Message */}
        <TextField
          fullWidth
          label="Greeting Message"
          value={greetingMessage}
          onChange={e => setGreetingMessage(e.target.value)}
          helperText="Custom message displayed on the birthday widget"
          sx={{ mb: 3 }}
        />

        {/* Theme Button */}
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<PaletteIcon />}
            onClick={() => navigate('/theme')}
            fullWidth
          >
            Customize Theme Colors
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            mt={1}
          >
            The birthday widget uses your studio&apos;s theme colors
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Save Button */}
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={isUpdating}
          fullWidth
        >
          {isUpdating ? 'Saving...' : 'Save Settings'}
        </Button>
      </Paper>

      {/* Embed Code */}
      {config?.embed_code && (
        <Card>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6">Embed Code</Typography>
              <Tooltip title={copySuccess ? 'Copied!' : 'Copy to clipboard'}>
                <IconButton onClick={handleCopyEmbedCode} color="primary">
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph>
              Copy this code to embed the birthday widget on your website:
            </Typography>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                overflowX: 'auto',
                mb: 2,
              }}
            >
              {config.embed_code}
            </Paper>

            {/* View Public Page Button */}
            {config.embed_code && (
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handleViewPublicPage}
                fullWidth
              >
                View Public Birthday Widget Page
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          Birthday widget settings saved successfully!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BirthdayWidgetSettings;
